import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  IRequirementSetDetail,
  IRequirementSetListItem,
  RequirementSetAction,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { convertToZonedDateTime, formatDateOnly } from "utils/dateUtils";
import { toDateInputValue, todayAsDateOnlyString } from "./dateOnlyField";
import styles from "./styles";
import { AuthoringPermissions } from "./authoringPermissions";
import LifecycleTrack from "./LifecycleTrack";
import {
  RequirementSetStatus,
  statusChipColor,
  statusOf,
} from "./requirementSetStatus";
import { trackableToday } from "./trackableToday";

type PublishBarProps = {
  set: IRequirementSetDetail;
  sets: IRequirementSetListItem[];
  permissions: AuthoringPermissions;
  busy: boolean;
  onSelectSet: (requirementSetId: string) => void;
  onNewSet: () => void;
  onTransition: (action: RequirementSetAction) => void;
};

/** Every action the caller may perform, and the right it needs. */
const actionRight = (
  action: RequirementSetAction,
  permissions: AuthoringPermissions
) => {
  switch (action) {
    case RequirementSetAction.SubmitForReview:
      return permissions.canEdit;
    case RequirementSetAction.Approve:
    case RequirementSetAction.ReturnToDraft:
      return permissions.canApprove;
    case RequirementSetAction.Archive:
    case RequirementSetAction.Unarchive:
      return permissions.canArchive;
    default:
      return false;
  }
};

const actionLabelKey = (action: RequirementSetAction) => {
  switch (action) {
    case RequirementSetAction.SubmitForReview:
      return "submitForReview";
    case RequirementSetAction.Approve:
      return "approve";
    case RequirementSetAction.Archive:
      return "archiveSet";
    // Both ways back to Draft read the same on screen; they differ only in the right they need.
    default:
      return "returnToDraft";
  }
};

const PublishBar = ({
  set,
  sets,
  permissions,
  busy,
  onSelectSet,
  onNewSet,
  onTransition,
}: PublishBarProps) => {
  const { formatMessage: t } = useIntl();
  const { licenseTrackerService } = useGlobalContext();
  const { organizationId, organizationName } = contextStore.useState();
  const status = statusOf(set.statusEnumValue);

  // The same key as the Tracker Availability pane, so a status changed there shows here at once. Only
  // a set that governs today can be trackable, so for any other the question is never asked.
  const availabilityResult = useQuery(
    ["licenseTrackerService.getLicenseTrackerAvailability", set.licenseId, organizationId],
    () => licenseTrackerService.getLicenseTrackerAvailability(set.licenseId!),
    { enabled: !!set.isActive && !!set.licenseId }
  );

  const trackable = trackableToday(
    set.isActive,
    availabilityResult.data,
    availabilityResult.isError
  );

  const trackableText = () => {
    switch (trackable.kind) {
      case "notGoverning":
        return t({ id: "no" });
      case "unknown":
        return "—";
      case "none":
        return organizationName
          ? t({ id: "noOrganizationTracksLicense" }, { organization: organizationName })
          : t({ id: "noOrganizationTracksThisLicense" });
      default:
        return trackable.names.join(", ");
    }
  };

  const banner = () => {
    if (status === RequirementSetStatus.Review) {
      return { severity: "warning" as const, key: "setLockedForReview" };
    }

    if (status === RequirementSetStatus.Archived) {
      return { severity: "error" as const, key: "setArchived" };
    }

    if (status !== RequirementSetStatus.Active) {
      return null;
    }

    if (set.isActive) {
      return { severity: "success" as const, key: "setGoverning" };
    }

    // Both sides compared as yyyy-MM-dd in UTC: a DateOnly's local string starts with a weekday, so
    // comparing it against a date string is always true.
    const endedAlready =
      !!set.endDate && toDateInputValue(set.endDate) < todayAsDateOnlyString();

    return endedAlready
      ? { severity: "warning" as const, key: "setSuperseded" }
      : { severity: "warning" as const, key: "setApprovedFuture" };
  };

  const activeBanner = banner();
  const offered = (set.allowedTransitions ?? []).filter((transition) =>
    actionRight(transition.action!, permissions)
  );

  return (
    <Paper variant="outlined" sx={styles.publishBar}>
      <Box sx={styles.publishBarTop}>
        <TextField
          select
          size="small"
          label={t({ id: "requirementSet" })}
          value={set.id ?? ""}
          onChange={(event) => onSelectSet(event.target.value)}
          sx={{ minWidth: 320 }}
        >
          {sets.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {`${option.name} · ${option.statusName}`}
            </MenuItem>
          ))}
        </TextField>

        {permissions.canEdit && (
          <Button startIcon={<AddIcon />} onClick={onNewSet} disabled={busy}>
            {t({ id: "newRequirementSet" })}
          </Button>
        )}

        <Box flexGrow={1} />

        <Chip
          size="small"
          color={statusChipColor(status)}
          label={set.statusName}
        />
      </Box>

      <Box sx={styles.publishBarBody}>
        <Box sx={styles.lifecycle}>
          <LifecycleTrack status={status} />

          <Box sx={styles.publishBarMeta}>
          <Typography variant="body2">
            {t(
              { id: "effectiveRange" },
              {
                from: formatDateOnly(set.effectiveDate) ?? "—",
                to: formatDateOnly(set.endDate) ?? t({ id: "openEnded" }),
              }
            )}
          </Typography>

          <Typography variant="body2">
            {set.approvedOn
              ? t(
                  { id: "approvedByOn" },
                  {
                    by: set.approvedBy ?? "—",
                    on: convertToZonedDateTime(set.approvedOn) ?? "—",
                  }
                )
              : t({ id: "notYetApproved" })}
          </Typography>

          <Typography variant="body2">
            {t(
              { id: "trackableToday" },
              { value: trackableText() }
            )}
          </Typography>
          </Box>
        </Box>

        <Box sx={styles.transitions}>
          {offered.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t({ id: "noTransitionsForRole" })}
            </Typography>
          )}

          {offered.map((transition) => (
            <Tooltip key={transition.action} title={transition.hint ?? ""}>
              <span>
                <Button
                  disabled={busy || !transition.isEnabled}
                  onClick={() => onTransition(transition.action!)}
                >
                  {t({ id: actionLabelKey(transition.action!) })}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {activeBanner && (
        <Alert severity={activeBanner.severity} sx={{ mt: 2 }}>
          {t(
            { id: activeBanner.key },
            {
              set: set.name ?? "",
              from: formatDateOnly(set.effectiveDate) ?? "",
              to: formatDateOnly(set.endDate) ?? "",
            }
          )}
        </Alert>
      )}
    </Paper>
  );
};

export default PublishBar;
